<template>
  <b-modal
    :id="id"
    :title="$translate(title)"
    hide-footer
    size="lg"
    centered
    content-class="rounded-8"
    @show="show"
    @hide="hide"
  >
    <div class="row">
      <div class="col-lg-6 col-sm-12 col-xs-12">
        <Input v-model="data.name" placeholder="Name" :required="true" />
      </div>
      <div class="col-lg-6 col-sm-12 col-xs-12">
        <Input v-model="data.address" placeholder="Address" />
      </div>
      <div class="col-lg-6 col-sm-12 col-xs-12">
        <Multiselect
          v-if="isUsersLoaded"
          :options="userOptions"
          :init-values="users"
          form-group-id="users"
          placeholder="users"
          placeholder-input="Select Users"
          :on-change="v => (users = v)"
        />
      </div>
      <div class="col-lg-6 col-sm-12 col-xs-12">
        <Select v-model="data.type" placeholder="Type" :options="types" />
      </div>
      <div class="col-lg-12 col-sm-12 col-xs-12" v-if="data.type === PDF_CONTENT_TYPE.LOGO">
        <div v-if="data.logo">
          <img :src="getFileURL('logo')" style="width: 100%; max-height: 300px; margin-bottom: 20px">
          <b-button variant="outline-primary" size="sm" class="btn-delete-device mb-4" @click="data.logo = null">
            {{ 'Upload new logo' | i18n }}
          </b-button>
        </div>
        <div v-else>
          <FileInput v-model="logo" placeholder="Company logo" fileExtensions="image/*" />
        </div>
      </div>
      <div class="col-lg-12 col-sm-12 col-xs-12 image-offset" v-if="data.type === PDF_CONTENT_TYPE.HEADER">
        {{ 'Recommended aspect ratio 1:7' | i18n }}
      </div>
      <div class="col-lg-12 col-sm-12 col-xs-12" v-if="data.type === PDF_CONTENT_TYPE.HEADER">
        <div v-if="data.headerFirst" class="image-offset">
          <img :src="getFileURL('headerFirst')" style="width: 100%; max-height: 300px" class="image-offset">
          <b-button variant="outline-primary" size="sm" class="btn-delete-device" @click="data.headerFirst = null">
            {{ 'Upload new header for the first page' | i18n }}
          </b-button>
        </div>
        <div v-else>
          <FileInput v-model="headerFirst" placeholder="First page header" fileExtensions="image/*" />
        </div>
      </div>
      <div class="col-lg-12 col-sm-12 col-xs-12" v-if="data.type === PDF_CONTENT_TYPE.HEADER">
        <div v-if="data.headerLast" class="image-offset">
          <img :src="getFileURL('headerLast')" style="width: 100%; max-height: 300px" class="image-offset">
          <b-button variant="outline-primary" size="sm" class="btn-delete-device" @click="data.headerLast = null">
            {{ 'Upload new header for pages' | i18n }}
          </b-button>
        </div>
        <div v-else>
          <FileInput v-model="headerLast" placeholder="Header following pages" fileExtensions="image/*" />
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-12 col-lg-12 col-sm-12 col-xs-12 d-flex justify-content-between">
        <b-button variant="outline-secondary" size="lg" class="btn-delete-device mr-3 px-125" @click="close">
          {{ 'Close' | i18n }}
        </b-button>
        <b-button variant="outline-primary" size="lg" class="btn-delete-device px-125" :disabled="!data.name" @click="onSubmit">
          {{ 'Save' | i18n }}
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import axios from 'axios';

import Input from '../../../common/input';
import FileInput from '../../../common/fileInput';
import Select from '../../../common/select';
import Multiselect from '../../../common/multiselect';
import { NotificationMixin, AuthStorageMixin, ReportsMixin } from '../../../../../mixins';
import { PDF_CONTENT_TYPE } from '../../../../../../../../constants';

export default {
  title: 'Add part',
  components: { Input, Select, FileInput, Multiselect },
  mixins: [NotificationMixin, AuthStorageMixin, ReportsMixin],
  props: {
    getPage: {
      type: Function,
      default: () => {},
    },
    onSave: {
      type: Function,
      default: () => {},
    },
    id: {
      type: String,
      default: '',
    },
    title: {
      type: String,
      default: '',
    },
    initData: {
      type: Object,
      default: () => {},
    },
    type: {
      type: String,
      default: 'add',
    },
  },
  data() {
    return {
      PDF_CONTENT_TYPE: PDF_CONTENT_TYPE,
      articles: [],
      data: {
        name: '',
        address: '',
        logo: '',
        headerFirst: '',
        headerLast: '',
        type: PDF_CONTENT_TYPE.LOGO,
      },
      types: [
        {
          value: PDF_CONTENT_TYPE.HEADER,
          text: 'Header',
        },
        {
          value: PDF_CONTENT_TYPE.LOGO,
          text: 'Logo',
        },
      ],
      logo: null,
      headerFirst: null,
      headerLast: null,
      preventCloseLocal: true,
      users: [],
      userOptions: [],
      isUsersLoaded: false,
    };
  },
  methods: {
    async show() {
      this.clear();
      const response = await this.reportsService.getUsersLookup();
      this.userOptions = response.data.map(item => ({ value: item.id, text: `${item.email} (${item.username})` }));

      if (this.type === 'edit') {
        this.data = Object.assign({}, this.initData);
        if (this.initData.users && this.initData.users.length) {
          this.users = this.initData.users.map((id) => this.userOptions.find(({ value }) => value === id));
        }
      }

      this.isUsersLoaded = true;
    },
    close() {
      this.$root.$emit('bv::hide::modal', this.id);
      this.clear();
    },
    clear() {
      this.data = {
        name: '',
        address: '',
        type: PDF_CONTENT_TYPE.LOGO,
      };
      this.logo = null;
      this.users = [];
      this.userOptions = [];
      this.isUsersLoaded = false;
    },
    hide(e) {
      const isChangedData = this.trackChangeData();
      if (this.preventCloseLocal && isChangedData) {
        let message = confirm(this.$translate('You have not saved your progress Are you sure you want to leave this page'));
        if (!message) {
          e.preventDefault();
        } else {
          this.clear();
        }
      } else {
        this.clear();
      }
    },
    trackChangeData() {
      let isChangedData = false;
      Object.keys(this.data).forEach((key) => {
        if (this.initData[key] !== this.data[key]) {
          isChangedData = true;
        }
      });
      return isChangedData;
    },
    async onSubmit() {
      try {
        this.preventCloseLocal = false;
        const formData = new FormData();
        formData.append('name', this.data.name);
        formData.append('address', this.data.address);
        formData.append('type', this.data.type);

        this.users.forEach((user) => {
          formData.append('users[]', user.value);
        });

        const appendFile = (name) => {
          if (this[name]) {
            formData.append(name, this[name]);
          } else {
            formData.append(name, this.data[name]);
          }
        };

        appendFile('logo');
        appendFile('headerFirst');
        appendFile('headerLast');

        await this.onSave(formData, this.data.id);
        this.$root.$emit('bv::hide::modal', this.id);
        await this.getPage();
        this.showNotification({ type: 'success', message: this.$translate('success') });
      } catch (err) {
        this.showNotification({ type: 'error', message: err.message });
        this.preventCloseLocal = true;
      }
    },
    getFileURL(name) {
      const auth = this.authStorageService.getAuth();
      return `${axios.defaults.baseURL}/api/v1/company/${this.initData.id}/file?name=${name}&dummy=${Math.random()}&authorization=${
        auth.access_token_link
      }`;
    },
  },
};
</script>

<style lang="scss" scoped>
.image-offset {
  margin-bottom: 20px;
}

::v-deep .modal-header {
  border: none;
  padding-bottom: 0px!important;
}

::v-deep .rounded-8 {
  border-radius: 8px;
}

.px-125 {
  padding-left: 125px;
  padding-right: 125px;
}

::v-deep .btn-lg {
  font-size: 16px;
  line-height: 24px;
}
</style>
